export const handleResponse = async (response: Response) => {
  const text = await response.text();
  let data: any = null;

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // A body-less success (200/201/204) has no JSON to parse — that's fine.
      // Only a non-empty, non-JSON body on a failed request is a real error.
      if (!response.ok) {
        throw new Error(`Server returned non-JSON response (${response.status})`);
      }
      return null;
    }
  }

  if (!response.ok) {
    const err: any = new Error(data?.message || `Request failed with status ${response.status}`);
    err.response = { status: response.status, data };
    // Attach Laravel validation errors as a readable string for debugging
    if (data?.errors) {
      err.validationErrors = data.errors;
      const detail = Object.entries(data.errors)
        .map(([k, v]) => `${k}: ${(v as string[]).join(", ")}`)
        .join(" | ");
      err.message = detail || err.message;
    }
    throw err;
  }

  return data;
};

/**
 * Flattens the `validationErrors` a failed request attaches (see above) into
 * a `{field: message}` map for form state. Every hook that submits a form
 * should read backend errors through this instead of re-deriving its own
 * shape from `err.message`/`err.response.data.errors` — those diverge from
 * what the backend actually sent and silently drop fields this doesn't.
 */
export const extractFieldErrors = (err: any): Record<string, string> => {
  const validationErrors = err?.validationErrors;
  if (!validationErrors) return {};

  return Object.fromEntries(
    Object.entries(validationErrors).map(([field, messages]) => [
      field,
      Array.isArray(messages) ? String(messages[0]) : String(messages),
    ]),
  );
};
